import { useCallback, useEffect, useState } from 'react';
import api from '../../hooks/useApi';
import { useToast } from '../ui/Toast';

// Which portal sections a client company has at all.
//
// This is a ceiling rather than a grant. Switching a section on here does not
// give anybody access: each contact is still switched on individually in their
// own Portal panel. Switching it off takes it from everyone at that client at
// once, and leaves their individual ticks alone so putting it back restores
// exactly who had it before.
export default function ClientPortalSections({ clientId }) {
  const [client, setClient] = useState(null);
  const [busy, setBusy] = useState(false);
  const { toast } = useToast();

  const load = useCallback(() => {
    api.get(`client-portal-access/client/${clientId}`)
      .then(r => setClient(r.client))
      .catch(() => setClient(null));
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  if (!client) return null;

  const toggle = async (f) => {
    setBusy(true);
    try {
      const r = await api.put(`client-portal-access/client/${clientId}/features`, {
        feature_key: f.key, enabled: !f.enabled,
      });
      setClient(r.client);
      toast.success(f.enabled
        ? `${f.label} switched off for this client, and for everyone at it`
        : `${f.label} available to this client`);
    } catch (e) {
      toast.error(e.data?.error || 'That did not work.');
    } finally { setBusy(false); }
  };

  return (
    <div className="flex flex-wrap items-center gap-1.5 mb-3">
      <span className="text-[8px] font-bold text-gray-400 uppercase tracking-wider mr-1">
        Portal sections available to this client
      </span>
      {client.features.map(f => (
        <button key={f.key} disabled={busy} onClick={() => toggle(f)}
          title={f.enabled
            ? `${f.label} can be switched on for individual contacts below.`
            : `${f.label} cannot be given to anybody here until this is on.`}
          className={`border rounded-full px-3 py-1 text-[9px] font-bold ${
            f.enabled ? 'bg-navy border-navy text-white' : 'bg-white border-gray-200 text-gray-500 hover:border-gray-300'
          }`}>
          {f.label}
        </button>
      ))}
      <span className="text-[8px] text-gray-400 ml-1">
        Switching one on here does not give anybody access; each contact is switched on separately.
      </span>
    </div>
  );
}
